import { useState, useEffect, useRef, useCallback } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"

interface WalletGenerationModalProps {
  open: boolean
  onClose: () => void
  onRetry: () => void
  redirectUrl: string | null
  // Apple polling mode (enabled when generationStatusUrl is provided)
  loyaltyCardId?: number | null
  generationStatusUrl?: string | null
  // Direct mode — parent controls state (for Google)
  status?: "generating" | "completed" | "failed"
  errorMessage?: string
  generatingTitle?: string
  // Fires when the wallet hand-off (redirect) happens.
  onCompleted?: () => void
  generatingDescription?: string
}

type GenerationState = "generating" | "completed" | "failed"

const POLL_INTERVAL_MS = 1000
const TIMEOUT_MS = 30000

export function WalletGenerationModal({
  open,
  onClose,
  onRetry,
  redirectUrl,
  loyaltyCardId,
  generationStatusUrl,
  status: externalStatus,
  errorMessage: externalError,
  onCompleted,
  generatingTitle = "Creating Your Loyalty Card",
  generatingDescription = "This will only take a moment...",
}: WalletGenerationModalProps) {
  const isPollingMode = !!generationStatusUrl
  const isInAppBrowser = /Instagram|FBAN|FBAV/.test(navigator.userAgent)

  const [internalState, setInternalState] =
    useState<GenerationState>("generating")
  const [internalError, setInternalError] = useState("")
  const [retryCount, setRetryCount] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(0)

  const displayState = isPollingMode
    ? internalState
    : externalStatus || "generating"
  const displayError = isPollingMode ? internalError : externalError || ""

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Polling mode (Apple)
  useEffect(() => {
    if (!isPollingMode) return
    if (!open || !generationStatusUrl || !loyaltyCardId || !redirectUrl) return

    setInternalState("generating")
    setInternalError("")
    startTimeRef.current = Date.now()

    const poll = async () => {
      if (Date.now() - startTimeRef.current >= TIMEOUT_MS) {
        stopPolling()
        setInternalError("Generation is taking too long. Please try again.")
        setInternalState("failed")
        return
      }

      try {
        const res = await fetch(
          `${generationStatusUrl}?loyalty_card_id=${loyaltyCardId}`
        )

        if (!res.ok) {
          stopPolling()
          setInternalError("Something went wrong. Please try again.")
          setInternalState("failed")
          return
        }

        const data = await res.json()

        if (data.status === "completed") {
          stopPolling()
          setInternalState("completed")
          if (!isInAppBrowser) {
            onCompleted?.()
            window.location.href = redirectUrl
            setTimeout(() => onClose(), 1500)
          }
        } else if (data.status === "failed") {
          stopPolling()
          setInternalError(
            data.error || "Something went wrong. Please try again."
          )
          setInternalState("failed")
        }
      } catch {
        stopPolling()
        setInternalError("Network error. Please try again.")
        setInternalState("failed")
      }
    }

    poll()
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS)

    return () => {
      stopPolling()
    }
  }, [
    open,
    generationStatusUrl,
    loyaltyCardId,
    redirectUrl,
    stopPolling,
    retryCount,
  ])

  // Direct mode (Google) — redirect on completed
  useEffect(() => {
    if (isPollingMode) return
    if (externalStatus === "completed" && redirectUrl) {
      onCompleted?.()
      window.location.href = redirectUrl
      setTimeout(() => onClose(), 1500)
    }
  }, [isPollingMode, externalStatus, redirectUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      stopPolling()
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] rounded-lg sm:max-w-sm"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {displayState === "generating" && (
          <div className="text-center py-4">
            <Loader2 className="h-12 w-12 mx-auto text-brand-strong animate-spin mb-4" />
            <DialogHeader>
              <DialogTitle>{generatingTitle}</DialogTitle>
              <DialogDescription>{generatingDescription}</DialogDescription>
            </DialogHeader>
          </div>
        )}

        {displayState === "completed" && (
          <div className="text-center py-4">
            <svg
              className="w-16 h-16 mx-auto text-green-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <DialogHeader>
              <DialogTitle>Your Card is Ready!</DialogTitle>
              {isPollingMode && isInAppBrowser && redirectUrl ? (
                <a
                  href={redirectUrl}
                  className="mt-6 inline-block bg-black text-white text-sm font-medium px-5 py-2.5 rounded-full"
                  onClick={() => {
                    onCompleted?.()
                    onClose()
                  }}
                >
                  Add to Apple Wallet
                </a>
              ) : (
                <DialogDescription>
                  Adding to your wallet now...
                </DialogDescription>
              )}
            </DialogHeader>
          </div>
        )}

        {displayState === "failed" && (
          <div className="text-center py-4">
            <svg
              className="w-16 h-16 mx-auto text-red-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <DialogHeader>
              <DialogTitle className="text-red-600">Oops!</DialogTitle>
              <DialogDescription>{displayError}</DialogDescription>
            </DialogHeader>
            <div className="flex gap-4 justify-center mt-6">
              <Button
                onClick={() => {
                  setRetryCount((c) => c + 1)
                  onRetry()
                }}
              >
                Retry
              </Button>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
